<template>
  <div class="ibox">
    <div class="ibox-title">
      <div class="row">
        <div class="col-xs-12 col-lg-4">
          <h1>
            {{ report.name }}<span v-if="rosterName !== ''">, {{ rosterName }}</span>
          </h1>
          <p class="m-b-none"><small>
            Report generated on {{ processedAt() }}
          </small></p>
        </div>
        <div class="col-xs-12 col-lg-4">
          <div class="m-t-sm flex align-items-center justify-end gap-sm w-fit text-green-600 text-12 rounded-xxs border border-green-600 border-solid">
              <div class="flex align-items-center w-fit gap-xs bg-green-600 rounded-tl-xxs rounded-bl-xxs p-y-xxs p-x-sm">  
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-green-50">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                </svg>
                <span class="text-heavy text-green-50">WIQ TIP</span>
              </div>
              <span class="p-r-sm">Click a column header to sort by that column</span>
            </div>
        </div>
        <div class="col-xs-12 col-lg-4 text-right">
          <div class="ibox-tools">
            <div class="m-t-sm flex align-items-center justify-end gap-x-lg">
              <div class="btn-group" v-if="hasCharts">
                <button class="btn btn-compact" :class="{ 'btn-primary': showingTable, 'btn-white': !showingTable }" type="button" @click="showingTable = true">
                  <i class="fa fa-table" aria-hidden="true"></i>&nbsp;<span class="hidden-xs">Table</span>
                </button>
                <button class="btn btn-compact" :class="{ 'btn-primary': !showingTable, 'btn-white': showingTable }" type="button" @click="showingTable = false">
                  <i class="fa fa-bar-chart" aria-hidden="true"></i>&nbsp;<span class="hidden-xs">Charts</span>
                </button>
              </div>
              <a class="btn btn-primary btn-compact" @click="downloadCSV()">
                <i class="fa fa-download"></i> <span class="hidden-xs">Download CSV</span></a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="ibox-content">
      <div class="table-responsive" v-if="showingTable">
        <table id="report-table" class="table">
          <thead>
            <tr>
              <th v-for="(header, index) in headers" 
                  @click="sortBy(index)" 
                  class="cursor-pointer"
                  :class="{ 'active-sort': sortColumn === index }"
              >
                <div class="flex align-items-center gap-x-sm">
                  <span> {{ header }} </span>
                  <span class="sort-indicator" v-if="sortColumn === index">
                    <i class="fa" :class="{ 'fa-sort-up': sortDirection === 'asc', 'fa-sort-down': sortDirection === 'desc' }"></i>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedRows">
              <td v-for="(item, index) in row" v-html="parseItem(item, index)">
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="!showingTable && hasCharts" class="grid grid-cols-2 sm-grid-cols-1">
        <div v-for="(chart, index) in charts">
          <highchart-wrapper :payload="chart.payload" :container-id="`chart-${index}`"></highchart-wrapper>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { downloadMix } from '../mixins/download_mix';
import { formattingMixin } from '../mixins/formatting_mix';
import { urlHelperMixin } from '../mixins/url_helper_mix';
import HighchartWrapper from './highchart_wrapper.vue';

// This row report is for server side generated reports. No fancy logic, it just throws down rows as given.
export default {
  name: 'row-report',
  components: { HighchartWrapper },
  mixins: [downloadMix, formattingMixin, urlHelperMixin],
  props: {
    report: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      headers: [],
      rows: [],
      columnMeta: {},
      charts: [],
      showingTable: true,
      sortColumn: null,
      sortDirection: 'asc',
    };
  },
  computed: {
    rosterName() {
      return this.report.result.roster_name ?? "";
    },
    hasCharts() {
      return this.charts.length > 0;
    },
    sortedRows() {
      if (this.sortColumn === null) return this.rows;
      
      return [...this.rows].sort((a, b) => {
        const aVal = this.getSortableValue(a[this.sortColumn]);
        const bVal = this.getSortableValue(b[this.sortColumn]);
        
        if (aVal === bVal) return 0;
        
        const modifier = this.sortDirection === 'asc' ? 1 : -1;
        
        if (aVal === null || aVal === '') return 1 * modifier;
        if (bVal === null || bVal === '') return -1 * modifier;
        
        return aVal < bVal ? -1 * modifier : 1 * modifier;
      });
    }
  },
  mounted() {
    let rows = this.report.result.rows.objects;
    this.columnMeta = this.report.result.rows.metadata ?? {};
    this.headers = rows.shift();
    this.rows = rows;

    this.charts = this.report.result.charts ?? [];
  },
  methods: {
    parseItem(item, index) {
      let metaKey = this.headers[index];
      let colMeta = this.columnMeta[metaKey];
      if (colMeta?.contains_link && item) {
        return this.urlify(item, false, colMeta?.icon_class);
      }

      return item;
    },
    processedAt() {
      const vm = this;
      return moment(vm.report.processed_at)
          .format('M/D/YYYY, hh:mm a');
    },
    downloadCSV() {
      const vm = this;

      const fileName = `${vm.report.name} ${vm.rosterName} - ${vm.processedAt()}.csv`;
      const rows = _.concat([vm.headers], vm.rows);
      this.downloadCSVFile(fileName, rows);
    },
    sortBy(columnIndex) {
      // If clicking the same column, toggle direction
      if (this.sortColumn === columnIndex) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New column, set to ascending
        this.sortColumn = columnIndex;
        this.sortDirection = 'asc';
      }
    },
    
    getSortableValue(value) {
      if (value === null || value === undefined) return null;
      
      // Handle different data types
      if (typeof value === 'number') return value;
      
      // Try to parse as date (e.g., "12/04/2024 11:10 am")
      if (typeof value === 'string' && value.match(/^\d{2}\/\d{2}\/\d{4}/)) {
        return new Date(value);
      }
      
      // Try to parse as number
      const numberValue = parseFloat(value);
      if (!isNaN(numberValue)) return numberValue;
      
      // Default to string comparison
      return value.toString().toLowerCase();
    }
  },
};
</script>

<style scoped>
.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.active-sort {
  background-color: #DAE2EC;
}

.sort-indicator {
  font-size: 0.8em;
  color: #48647F;
}
</style>
